import React from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useAuthStore from '../../store/authStore';
import { adminApi } from '../../services/api';
import type { Plant } from '../../types';

interface NavItem {
  to:         string;
  label:      string;
  icon:       string;
  permission: string | null;
}

const NAV: NavItem[] = [
  { to: '/',           label: 'Dashboard',  icon: '⚡', permission: null },
  { to: '/alarms',     label: 'Alarms',     icon: '🔔', permission: 'alarms' },
  { to: '/anomaly',    label: 'Anomaly',    icon: '🧠', permission: 'anomaly' },
  { to: '/historical', label: 'Historical', icon: '📈', permission: 'historical' },
  { to: '/hydrology',  label: 'Hydrology',  icon: '🌊', permission: 'hydrology' },
  { to: '/planning',   label: 'ERC Declarations', icon: '📋',  permission: 'planning' },
  { to: '/invoice',    label: 'Invoice',    icon: '🧾',  permission: 'invoice' },
  { to: '/shift-log',  label: 'Shift Log',  icon: '📝',  permission: 'reports' },
  { to: '/admin',      label: 'Admin',      icon: '⚙️',  permission: 'admin' },
  { to: '/platform',   label: 'Platform',   icon: '🏭',  permission: 'super_admin' },
];

export default function Sidebar() {
  const { user, hasPermission } = useAuthStore();

  const { data: plantData } = useQuery<{ plant: Plant | null }>({
    queryKey: ['my-plant', user?.id],
    queryFn:  () => adminApi.plant().then(r => r.data),
    staleTime: 10 * 60_000,
    enabled:  user?.role !== 'super_admin',
  });

  const plantName = plantData?.plant?.name ?? null;

  return (
    <aside className="w-60 flex-shrink-0 bg-hydro-card border-r border-hydro-border flex flex-col">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-hydro-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-lg">💧</div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 leading-tight truncate">
              {user?.role === 'super_admin' ? 'Hydro Monitor' : (plantName ?? 'Loading…')}
            </p>
            <p className="text-xs text-slate-500">
              {user?.role === 'super_admin' ? 'All Plants' : 'Plant Control System'}
            </p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map(({ to, label, icon, permission }) => {
          if (permission && !hasPermission(permission)) return null;
          // Super admin only needs Dashboard overview + Platform admin
          if (user?.role === 'super_admin' && permission && permission !== 'super_admin') return null;
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-brand-600/15 text-brand-600 border border-brand-500/30'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              <span className="text-base">{icon}</span>
              {label}
            </NavLink>
          );
        })}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-hydro-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-sm font-bold text-white">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">
              {user?.role === 'plant_operator' ? 'Plant Operator'
                : user?.role === 'super_admin' ? 'Super Admin'
                : user?.role?.replace(/_/g, ' ')}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
